import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { useNavigate } from 'react-router';

import { Chip, Stack } from '@mui/material';
import { CustomPopover, usePopover, useProfileProgressStore, useRouter, useSettingsContext, useSnackbar, useUserDetailsStore, varHover } from '@zeta-core';
import { LogOutIcon } from '@zeta-core/assets';
import { RouterLinks } from '../../app-route-paths';
import { signOut } from '../../auth/jwt';

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();
  const navigate = useNavigate();
  const theme = useTheme();
  const settings = useSettingsContext();
  const user = useUserDetailsStore((state) => state.user);
  const { firstName, lastName, role } = user;
  const profileProgressStore = useProfileProgressStore((state) => state);

  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  const handleThemeToggle = () => {
    popover.onClose();
    settings.onUpdate('themeMode', settings.themeMode === 'dark' ? 'light' : 'dark');
  };

  const handleLogout = async () => {
    try {
      await signOut();
      profileProgressStore.updateProfileSetupStatus(null, false);
      popover.onClose();
      router.replace('/');
    } catch (error) {
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleChangePassword = () => {
    popover.onClose();
    navigate(RouterLinks.changePassword, {
      state: {
        userNameOrEmail: user?.userName || user?.username || user?.email || user?.emailAddress || user?.login || '',
      },
    });
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          alt={firstName}
          sx={{
            width: 36,
            height: 36,
            bgcolor: theme.palette.background.default,
            color: theme.palette.action.active,
          }}
        >
          {firstName?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <CustomPopover {...popover} sx={{ width: 350, p: 0 }}>
        <Stack
          sx={{
            p: 2,
            pb: 1.5,
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
          gap={1}
        >
          <Avatar sx={{ width: 62, height: 62, background: theme.palette.grey[300] }}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.grey[600] }}>
              {firstName?.charAt(0).toUpperCase()}
              {lastName?.charAt(0).toUpperCase()}
            </Typography>
          </Avatar>
          <Typography variant="h6" noWrap>
            {firstName} {lastName}
          </Typography>
          <Chip label={role === 'Admin' ? 'Super Admin' : role} variant="soft" size="small" />
        </Stack>

        <Divider sx={{ borderStyle: 'dashed', mx: 1 }} />

        <Stack m={2} sx={{ cursor: 'pointer' }} onClick={handleThemeToggle}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Stack direction="row" alignItems="center" gap={1}>
              {settings.themeMode === 'dark' ? <LightModeRoundedIcon fontSize="small" /> : <DarkModeRoundedIcon fontSize="small" />}
              <Typography>Display Theme</Typography>
            </Stack>
            <Chip size="small" variant="soft" label={settings.themeMode === 'dark' ? 'Dark' : 'Light'} />
          </Stack>

          <Typography variant="caption" color={'text.disabled'} pt={1}>
            Switch the portal between light mode and dark mode.
          </Typography>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed', mx: 1 }} />

        <MenuItem onClick={handleChangePassword} sx={{ my: 1, mx: 2, justifyContent: 'center' }}>
          <Typography variant="body2">Change Password</Typography>
        </MenuItem>

        <Divider sx={{ borderStyle: 'dashed', mx: 1 }} />

        <MenuItem onClick={handleLogout} sx={{ my: 1, mx: 2, fontWeight: 'fontWeightBold', color: 'error.main', justifyContent: 'center' }}>
          <LogOutIcon fill={theme.palette.error.main} />
          <Typography variant="body2"> Logout</Typography>
        </MenuItem>
      </CustomPopover>
    </>
  );
}
